import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getEmployeesStart,
  getEmployeeByUiidStart,
  setEmployeeProfileUiid,
} from "../../../../redux/slices/employee/employeeSlice";
import OverviewCard, {
  InnerCard,
} from "../../../../components/Module/Employees/OverviewCard";

import {
  AdvSeparator,
  Avatar,
  Backdrop,
  Button,
  CopyToClipboard,
  Divider,
  DropdownMenu,
  DropdownMenuNew,
  EmployeeHeader,
  InfoCard,
  InfoIcon,
  InfoText,
  LogoLoader,
  NewTab,
  NewTable,
  NoData,
  PageHeader,
  PageTab,
  PageTitle,
  Separator,
  SignatureCard,
  StatusTags,
  TableAdv,
  Tag,
} from "../../../../components";
import { mapTableRows } from "../../../../utils/mapTableRows";
import Notes from "./Overview/Notes";
import Activities from "./Overview/Activities";
import Mails from "./Overview/Mails";
import TwoWaySyncMails from "./Overview/TwoWaySyncMails";
import { getValueByKey } from "../../../../utils/dataMappings";
import { getEmployeeId } from "../../../../utils/getEmployeeId";
import { usePagination } from "../../../../hooks";
import { Link, useNavigate } from "react-router-dom";
import { dateFormator } from "../../../../utils";
import { employeeOverviewApi } from "../../../../redux/slices/employee/employeeApi";
import CustomEmail from "../../../../components/FormFields/Custom/CustomEmail";
import { number } from "framer-motion";
import {
  EmployeeStatus,
  H1BStatus,
  LcaStatus,
  PassportStatus,
  TimesheetStatus,
  WADocStatus,
  WorkLocationType,
} from "../../../../assets/data/enumData";
import { formatCurrency, formatPhone } from "../../../../utils/stringUtils";
import dayjs from "dayjs";
import { ENDPOINTS } from "../../../../redux/apiConfig";
import axiosRequest from "../../../../redux/slices/universal/axiosService";

const poDetailsFromClientDocs = async (placementId) => {
  const res = axiosRequest({
    method: "GET",
    url: ENDPOINTS.DOCUMENT.GET_ALL,
    params: {
      coll: "Document",
      fil: JSON.stringify({
        docType: { type: "IN", value: ["Po", "PO"] },
        "parent.placement": { type: "IN", value: [placementId] },
      }),
      sort: JSON.stringify({ addedOn: -1 }),
    },
  });
  return res;
};

const tabContent = (size = "text-sm", placement, poDetails) => (
  <div className={`${size} text-gray-700 space-y-2 pt-3 mt-3`}>
    <Divider className="mb-5" />

    <div className="flex items-center flex-wrap text-black text-sm">
      {/* <span>{poDetails?.file?.[0]?.name}</span> */}
      <span>PO</span>
      <span className="mx-2 text-gray-400">|</span>
      <span>
        Start Date:{" "}
        {placement?.jobInfo?.endDate
          ? dayjs(placement?.jobInfo?.startDate).format("MM/DD/YYYY")
          : "-"}
      </span>
      <span className="mx-2 text-gray-400">|</span>
      <span>
        End Date:{" "}
        {poDetails?.file?.[0]?.expiry
          ? dayjs(poDetails?.file?.[0]?.expiry).format("MM/DD/YYYY")
          : "-"}
      </span>
    </div>

    <div className="text-sm mt-5 flex items-center">
      <span>
        Bill Rate:{" "}
        {formatCurrency(
          placement?.payments?.[placement?.payments?.length - 1]?.billRate
            ?.rate,
        )}
      </span>
      <span className="mx-2 text-gray-400">|</span>
      <span>
        Pay Rate:{" "}
        {formatCurrency(
          placement?.payments?.[placement?.payments?.length - 1]?.payRate
            ?.payRatePerHour,
        )}
      </span>
      <span className="mx-2 text-gray-400">|</span>
      <span>
        Net Term:{" "}
        {placement?.jobInfo?.billClient?.generalInfo?.placementDetails
          ?.recurring?.netTerms ??
          placement?.jobInfo?.billClient?.generalInfo?.placementDetails
            ?.permanent?.netTerms}
      </span>
    </div>
    <div className="flex justify-end mt-1">
      <Tag
        title={poDetails?.file?.[0]?.expiry ? 
          dayjs(poDetails?.file?.[0]?.expiry).isAfter(dayjs())
            ? `${dayjs(poDetails?.file?.[0]?.expiry).diff(
                dayjs(),
                "day",
              )}  Days Left`
            : `${dayjs(poDetails?.file?.[0]?.expiry).diff(
                dayjs(),
                "day",
              )}  Days Over`
          : "-"
        }
        variant="outlined"
        color="danger"
        size="sm"
        className="bg-primary-100/50"
      />
    </div>
  </div>
);

const Overview = () => {
  const [overviewData, setOverviewData] = useState({});
  console.log({ overviewData });
  const [overviewLoading, setOverviewLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    employeeProfileUiid,
    currentEmployee: { employeeDetails, loadedTabs },
    loading,
  } = useSelector((state) => state.employee);

  const isLoading = loading;

  useEffect(() => {
    const fetchOverview = async () => {
      const startTime = performance.now();
      try {
        setOverviewLoading(true);
        const data = await employeeOverviewApi(employeeProfileUiid);
        //mapping
        const overviewRes = {
          placements: data?.placements?.value?.data?.data,
          lca: data?.lca?.value?.data?.data,
          h1b: data?.h1b?.value?.data?.data,
          documents: data?.documents?.value?.data?.data,
          timesheets: data?.timesheets?.value?.data?.data,
          invoiceKpi: data?.invoiceKpi?.value?.data?.data?.[0] || {},
          payrolls: data?.payrolls?.value?.data?.data,
        };
        //data uniqueness
        //p1placement for worklocation card
        const placementP1 =
          overviewRes?.placements?.find(
            (item) => item.clientPlacement.placement === "P1",
          ) || {};
        // address from lca for POE address
        let numberOfAddressesInLca =
          overviewRes?.lca?.placeOfEmployment?.length || 0;
        let addressesInLca = overviewRes?.lca?.placeOfEmployment || [];
        let homeLocation = "-";
        let clientLocation = "-";
        let currentLocation = placementP1?.clientPlacement?.workLocation
          ?.address?.city
          ? (placementP1?.clientPlacement?.workLocation?.address?.city || "") +
            ", " +
            (placementP1?.clientPlacement?.workLocation?.address?.state || "") +
            ", " +
            (placementP1?.clientPlacement?.workLocation?.address?.country || "")
          : "-";
        if (numberOfAddressesInLca === 1) {
          let endClient = addressesInLca[0]?.endClient || "";
          let address = addressesInLca[0]?.address;
          if (endClient !== "Home Address") {
            clientLocation = address?.city
              ? (address?.city || "") +
                ", " +
                (address?.state || "") +
                ", " +
                (address?.country || "")
              : "-";
          } else {
            homeLocation = address?.city
              ? (address?.city || "") +
                ", " +
                (address?.state || "") +
                ", " +
                (address?.country || "")
              : "-";
          }
        } else if (numberOfAddressesInLca >= 2) {
          clientLocation = addressesInLca[0]?.address?.city
            ? (addressesInLca[0]?.address?.city || "") +
              ", " +
              (addressesInLca[0]?.address?.state || "") +
              ", " +
              (addressesInLca[0]?.address?.country || "")
            : "-";
          homeLocation = addressesInLca[1]?.address?.city
            ? (addressesInLca[1]?.address?.city || "") +
              ", " +
              (addressesInLca[1]?.address?.state || "") +
              ", " +
              (addressesInLca[1]?.address?.country || "")
            : "-";
        }
        //data cards
        const result = homeLocation.localeCompare(currentLocation);
        const workLocationCard = {
          workLocation:
            WorkLocationType?.find(
              (item) =>
                item.value === placementP1?.clientPlacement?.workLocation?.type,
            )?.label || null,
          locationMatched: result,
          locations: [
            { name: "Home Location", value: homeLocation },
            { name: "Client Location", value: clientLocation },
            { name: "Current Location", value: currentLocation },
          ],
        };
        //lca card for showing payroll data in lca card
        const lcaAmount = overviewRes?.lca?.salaryInfo?.wageRate || 0;
        const ytdAmount =
          overviewRes?.payrolls?.reduce(
            (acc, item) => acc + item?.amounts.netPay,
            0,
          ) || 0;
        const lcaCard = {
          payPeriodDate: overviewRes?.payrolls?.sort(
            (a, b) => new Date(b.payDay) - new Date(a.payDay),
          )?.[0]?.payDay
            ? dayjs(
                overviewRes?.payrolls?.sort(
                  (a, b) => new Date(b.payDay) - new Date(a.payDay),
                )?.[0]?.payDay,
              )
                .subtract(1, "month")
                .format("MMMM, YYYY")
            : "-",
          lcaAmount: formatCurrency(lcaAmount),
          ytdAmount: formatCurrency(ytdAmount),
          // dueAmount: formatCurrency(lcaAmount - ytdAmount),
          dueAmount: ytdAmount - lcaAmount,
        };
        //from here all the cards for immigration section mapping
        const h1bDetailCard = overviewRes?.h1b?.receiptNum
          ? {
              title: "H-1B",
              number: overviewRes?.h1b?.receiptNum || "-",
              status:
                overviewRes?.h1b?.recordStatus === 1
                  ? "Active"
                  : overviewRes?.h1b?.recordStatus === 2
                    ? "Inactive"
                    : overviewRes?.h1b?.recordStatus === 3
                      ? "Extension"
                      : "-" || "-",
              firstLabel: "Filed On",
              firstValue: overviewRes?.h1b?.filedOn
                ? dayjs(overviewRes?.h1b?.filedOn).format("MM/DD/YYYY")
                : "-",
              secondLabel: "Expiry Date",
              secondValue: overviewRes?.h1b?.expiryDate
                ? dayjs(overviewRes?.h1b?.expiryDate).format("MM/DD/YYYY")
                : "-",
              daysLeft: overviewRes?.h1b?.expiryDate
                ? `${dayjs(overviewRes?.h1b?.expiryDate).diff(
                    dayjs(),
                    "day",
                  )} days`
                : "-",
              getDaysColor:
                dayjs(overviewRes?.h1b?.expiryDate).diff(dayjs(), "day") < 0
                  ? "danger"
                  : "success",
            }
          : null;
        const lcaDetailCard = overviewRes?.lca?.uiid
          ? {
              title: "LCA",
              number: overviewRes?.lca?.number || "-",
              status:
                LcaStatus?.find(
                  (item) => item.value === overviewRes?.lca?.status,
                )?.label || "-",
              firstLabel: "Start Date",
              firstValue: overviewRes?.lca?.startDate
                ? dayjs(overviewRes?.lca?.startDate).format("MM/DD/YYYY")
                : "-",
              secondLabel: "End Date",
              secondValue: overviewRes?.lca?.endDate
                ? dayjs(overviewRes?.lca?.endDate).format("MM/DD/YYYY")
                : "-",
              daysLeft: `${dayjs(overviewRes?.lca?.endDate).diff(
                dayjs(),
                "day",
              )} days`,
              getDaysColor:
                dayjs(overviewRes?.lca?.endDate).diff(dayjs(), "day") < 0
                  ? "danger"
                  : "success",
            }
          : null;
        const latestI94 =
          employeeDetails?.i94?.[employeeDetails?.i94?.length - 1] || null;
        const i94DetailCard = latestI94
          ? {
              title: "I-94",
              status:
                WADocStatus?.find((item) => item.value === latestI94?.status)
                  ?.label || "-",
              firstLabel: "Start Date",
              firstValue: latestI94?.doe
                ? dayjs(latestI94?.doe).format("MM/DD/YYYY")
                : "-",
              secondLabel: "End Date",
              secondValue: latestI94?.admitDate
                ? dayjs(latestI94?.admitDate).format("MM/DD/YYYY")
                : "-",
              daysLeft: latestI94?.admitDate
                ? `${dayjs(latestI94?.admitDate).diff(dayjs(), "day")} days`
                : "-",
              getDaysColor: latestI94?.admitDate
                ? dayjs(latestI94?.admitDate).diff(dayjs(), "day") < 0
                  ? "danger"
                  : "success"
                : "",
            }
          : null;
        const latestEverify =
          employeeDetails?.eVerify?.[employeeDetails?.eVerify?.length - 1] ||
          null;
        const eVerifyDetailCard = latestEverify
          ? {
              title: "E-Verify",
              status:
                latestEverify?.status === 1
                  ? "Active"
                  : latestEverify?.status === 2
                    ? "Inactive"
                    : "-",
              // firstLabel: "E-Verification Date",
              // firstValue: latestEverify?.date
              //   ? dayjs(latestEverify?.date).format("MM/DD/YYYY")
              //   : "-",
              secondLabel: "End Date",
              secondValue: latestEverify?.expiryDate
                ? dayjs(latestEverify?.expiryDate).format("MM/DD/YYYY")
                : "-",
              daysLeft: latestEverify?.expiryDate
                ? `${dayjs(latestEverify?.expiryDate).diff(
                    dayjs(),
                    "day",
                  )} days`
                : "-",
              getDaysColor: latestEverify?.expiryDate
                ? dayjs(latestEverify?.expiryDate).diff(dayjs(), "day") < 0
                  ? "danger"
                  : "success"
                : "",
            }
          : null;
        const immigrationCards = [
          h1bDetailCard,
          lcaDetailCard,
          i94DetailCard,
          eVerifyDetailCard,
        ];
        // from here documents sectio including al;l its detail card
        const latestI9 =
          overviewRes?.documents?.find((item) => item.docType === "I9") || null;
        const i9DetailCard = latestI9
          ? {
              title: "I9",
              secondLabel: "End Date",
              secondValue: latestI9?.docExpiry
                ? dayjs(latestI9?.docExpiry).format("MM/DD/YYYY")
                : "-",
              daysLeft: latestI9?.docExpiry
                ? `${dayjs(latestI9?.docExpiry).diff(dayjs(), "day")} days`
                : "-",
              status:
                WADocStatus?.find((item) => item.value === latestI9?.docStatus)
                  ?.label || "-",
              getDaysColor: latestI9?.docExpiry
                ? dayjs(latestI9?.docExpiry).diff(dayjs(), "day") < 0
                  ? "danger"
                  : "success"
                : "",
            }
          : null;
        const latestDrivingLicense =
          overviewRes?.documents?.find(
            (item) => item.docType === "Driving License",
          ) || null;
        const drivingLicenseDetailCard = latestDrivingLicense
          ? {
              title: "Driving License",
              number: latestDrivingLicense?.title,
              firstLabel: "Start Date",
              firstValue: latestDrivingLicense?.addedOn
                ? dayjs(latestDrivingLicense?.addedOn).format("MM/DD/YYYY")
                : "-",
              secondLabel: "End Date",
              secondValue: latestDrivingLicense?.docExpiry
                ? dayjs(latestDrivingLicense?.docExpiry).format("MM/DD/YYYY")
                : "-",
              daysLeft: latestDrivingLicense?.docExpiry
                ? `${dayjs(latestDrivingLicense?.docExpiry).diff(
                    dayjs(),
                    "day",
                  )} days`
                : "-",
              status:
                WADocStatus?.find(
                  (item) => item.value === latestDrivingLicense?.docStatus,
                )?.label || "-",

              getDaysColor: latestDrivingLicense?.docExpiry
                ? dayjs(latestDrivingLicense?.docExpiry).diff(dayjs(), "day") <
                  0
                  ? "danger"
                  : "success"
                : "",
            }
          : null;
        const latestPassport =
          employeeDetails?.passport?.[employeeDetails?.passport?.length - 1] ||
          null;
        const passportDetailCard = latestPassport
          ? {
              title: "Passport Number",
              number: latestPassport?.number,
              firstLabel: "Start Date",
              firstValue: latestPassport?.issueDate
                ? dayjs(latestPassport?.issueDate).format("MM/DD/YYYY")
                : "-",
              secondLabel: "End Date",
              secondValue: latestPassport?.expiryDate
                ? dayjs(latestPassport?.expiryDate).format("MM/DD/YYYY")
                : "-",
              daysLeft: latestPassport?.expiryDate
                ? `${dayjs(latestPassport?.expiryDate).diff(
                    dayjs(),
                    "day",
                  )} days`
                : "-",
              status: PassportStatus?.find(
                (item) => item.value === latestPassport?.status,
              )?.label,
              getDaysColor: latestPassport?.expiryDate
                ? dayjs(latestPassport?.expiryDate).diff(dayjs(), "day") < 0
                  ? "danger"
                  : "success"
                : "",
            }
          : null;
        const documentsCards = [
          i9DetailCard,
          drivingLicenseDetailCard,
          passportDetailCard,
        ];
        // from here placement card
        const placementCardsTabData = await Promise.all(
          overviewRes?.placements?.map(async (item) => {
            let poDetails = {};
            try {
              const doc = await poDetailsFromClientDocs(item?.uiid);
              poDetails =
                doc?.data?.data?.sort(
                  (a, b) => new Date(b?.addedOn) - new Date(a?.addedOn),
                )?.[0] || {};
            } catch (error) {
              console.error("poDetailsFromClientDocs failed:", error);
            }
            return {
              title: item?.clientPlacement?.placement,
              type: "main",
              page: tabContent("text-xs", item, poDetails),
            };
          }) || [],
        );

        const placements = overviewRes?.placements
          ?.map((item) => item?.clientPlacement?.placement)
          .filter(Boolean);
        const uniquePlacements = [...new Set(placements)];
        const timesheetsMappedWithPlacement = uniquePlacements?.map((item) => ({
          placement: item,
          timesheets: overviewRes?.timesheets
            ?.filter(
              (item2) =>
                item2?.placementId?.clientPlacement?.placement === item,
            )
            ?.sort(
              (a, b) => new Date(b?.period?.from) - new Date(a?.period?.from),
            ),
        }));
        const timesheetCards =
          timesheetsMappedWithPlacement?.map((item) => {
            const filteredTimesheets = item?.timesheets || [];
            return {
              title: item?.placement,
              data: filteredTimesheets?.map((item2) => {
                return {
                  ...item2,
                  month: `${dayjs()
                    .month(item2?.period?.month - 1)
                    .format("MMM")}, ${item2?.period?.year}`,
                  hours: item2?.dailyEntries?.reduce(
                    (acc, item3) => acc + item3?.st,
                    0,
                  ),
                  Invoice: item2?.invoiceId?.map((item4) => item4?.invoiceNum),
                  status: TimesheetStatus?.find(
                    (item3) => item3.value === item2?.status,
                  )?.label,
                  color: "success",
                };
              }),
            };
          }) || [];
        const invoiceCards = {
          "Total Amount Invoiced": formatCurrency(
            overviewRes?.invoiceKpi?.invoiced,
          ),
          "Total Amount Received": formatCurrency(
            overviewRes?.invoiceKpi?.received,
          ),
          "Due Amount": formatCurrency(overviewRes?.invoiceKpi?.due),
        };
        setOverviewData({
          workLocationCard,
          lcaCard,
          invoiceCards,
          timesheetCards,
          immigrationCards: immigrationCards?.filter(Boolean) || [],
          documentsCards: documentsCards?.filter(Boolean) || [],
          placementCardsTabData,
        });
        return data;
      } catch (error) {
        console.error("Error in fetchOverview Section Employee", error);
      } finally {
        setOverviewLoading(false);
        const endTime = performance.now();
        // console.assert(
        //   import.meta.env.VITE_APP_ENV !== "local",
        //   `Time complexity: ${Number(endTime - startTime).toFixed(2)}ms`,
        // );
      }
    };
    fetchOverview();
  }, [employeeDetails, employeeProfileUiid]);

  const handleEdit = (row) => {
    alert(row.slno);
  };

  useEffect(() => {
    if (employeeProfileUiid && !loadedTabs.includes("overview")) {
      dispatch(
        getEmployeeByUiidStart({
          uiid: employeeProfileUiid,
          tab: "overview",
          ref: "Parent",
          incl: ["firstName middleName lastName employmentType uiid"],
          ret: "uiid profilePhotoUrl firstName middleName lastName jobTitle status employmentType email mobile workAuthorization ssn dob joinDate workLocation bloodType employmentType linkedTo placements passport i94 eVerify onboardId",
        }),
      );
    }
  }, [employeeProfileUiid, loadedTabs, dispatch]);

  const {
    profilePhotoUrl,
    firstName,
    middleName,
    lastName,
    jobTitle,
    status,
    joinDate,
    uiid,
    employmentType,
    email,
    mobile,
    workAuthorization,
  } = employeeDetails || {};

  return (
    <div className="mt-3 flex-1 flex flex-col gap-2 overflow-hidden">
      {/* Shared wrapper so PageHeader and grid align */}
      <div>
        <PageHeader
          Section={<EmployeeHeader full />}
          RightSection={
            <div className="flex gap-2 justify-between items-center">
              <Button
                color="primary"
                size="sm"
                title={"Back"}
                icon={"west"}
                type="link"
                to="/employee"
              />
              <DropdownMenuNew
                MenuSection={
                  <div className="p-1">
                    <Link
                      to="/e-onboarding?e-onboarding=initiate-onboarding#initiate-onboarding"
                      state={{ employeeId: employeeProfileUiid }}
                    >
                      <Button
                        color="dark"
                        title={"Initiate Onboarding"}
                        variant="text"
                        size="sm"
                        className="w-full text-left"
                        // onClick={() => navigate("/e-onboarding")}
                      />
                    </Link>
                    {/* Removed Button Navigation for Initiate Onboarding */}
                    {/* <Button
                      color="dark"
                      title={"Initiate Offboarding"}
                      variant="text"
                      size="sm"
                      className="w-full text-left"
                    /> */}
                  </div>
                }
              >
                <Button
                  color="primary"
                  size="sm"
                  title={"More"}
                  icon="keyboard_arrow_down"
                  iconPosition="after"
                />
              </DropdownMenuNew>
            </div>
          }
          rightSectionClassName="flex-1 max-w-[170px]"
        />
      </div>

      <div className="flex-1 p-1 flex flex-col gap-4 mt-4 overflow-auto">
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-2 2xl:grid-cols-[2fr_2fr_3fr]">
          <div className="order-1">
            {/* Column 1 */}
            <OverviewCard
              title="Work Location"
              RightSection={
                <>
                  <StatusTags
                    tag={overviewData?.workLocationCard?.workLocation}
                  />
                  <StatusTags
                    tag={
                      overviewData?.workLocationCard?.locationMatched
                        ? "Not Matched"
                        : "Matched"
                    }
                    icon
                  />
                </>
              }
            >
              <div className="space-y-2 mb-8  w-fit">
                {(overviewData?.workLocationCard?.locations || []).map(
                  (loc, index) => (
                    <div
                      key={index}
                      className="body-two grid grid-cols-[7fr_2fr_7fr] items-center"
                    >
                      <span className="font-medium">
                        {loc.name.replace(/([A-Z])/g, " $1")}
                      </span>
                      <span className="text-center">:</span>
                      <span className="info-message font-normal">
                        {loc.value || "—"}
                      </span>
                    </div>
                  ),
                )}
              </div>
            </OverviewCard>
          </div>
          <div className="order-2">
            <OverviewCard title="LCA">
              <div className="space-y-2 w-fit">
                {/* Regular LCA items */}
                {[
                  {
                    label: "Pay Period Date",
                    value: overviewData?.lcaCard?.payPeriodDate,
                  },
                  {
                    label: "LCA Amount",
                    value: overviewData?.lcaCard?.lcaAmount,
                  },
                  {
                    label: "YTD Amount",
                    value: overviewData?.lcaCard?.ytdAmount,
                  },
                ]?.map((item, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[7fr_3fr_7fr] items-center text-sm"
                  >
                    {/* Label */}
                    <span className="body-two ">{item.label}</span>
                    <span className="text-center">:</span>
                    <span className="text-[16px] leading-[20px] font-medium">
                      {item.value}
                    </span>
                  </div>
                ))}

                {/* Divider */}
                <Divider />

                {/* Due Amount */}
                <div className="grid grid-cols-[7fr_2fr_7fr] items-center text-sm text-primary font-semibold ">
                  <span className="subHeadline font-medium">Due Amount</span>
                  <span className="text-center">:</span>
                  <span className="subHeadline font-medium">
                    {formatCurrency(overviewData?.lcaCard?.dueAmount)}
                  </span>
                </div>
              </div>
            </OverviewCard>
          </div>
          <div className="order-3 lg:col-span-2 xl:col-span-1">
            {/* Column 3 */}
            <OverviewCard title="Person Information">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <Avatar size="xl" src={profilePhotoUrl} alt={firstName} />

                  <div className="flex flex-col justify-center">
                    <div className="flex items-center gap-1">
                      <span className="card-title">
                        {`${firstName} ${middleName || ""} ${lastName || ""}`}
                      </span>
                      {(firstName || lastName) && (
                        <CopyToClipboard
                          content={`${firstName} ${middleName || ""} ${
                            lastName || ""
                          }`}
                          title="Full Name"
                          description="Copy Name"
                          tooltipPosition="top"
                          color="dark"
                          size="xs-sm"
                        />
                      )}
                    </div>

                    <p className="text-gray-600 text-[14px]">
                      {jobTitle || "—"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center flex-wrap gap-2">
                  <Tag
                    title={getEmployeeId(employeeProfileUiid, employmentType)}
                    color="warning"
                    variant="outlined"
                  />

                  {/* Email */}
                  <div className="flex items-center gap-1">
                    <Button
                      icon="mail"
                      color="dark"
                      size="xs-sm"
                      type="icon"
                      onClick={() => window.open(`mailto:${email?.primary}`)}
                    />
                    <span className="font-roboto">{email?.primary || "-"}</span>
                    {email?.primary && (
                      <CopyToClipboard
                        content={email.primary}
                        title="Email"
                        description="Copy Email"
                        tooltipPosition="top"
                        color="dark"
                        size="xs-sm"
                      />
                    )}
                  </div>

                  <Divider orientation="vertical" />

                  {/* Mobile */}
                  <div className="flex items-center gap-1">
                    <Button
                      icon="call"
                      color="dark"
                      size="xs-sm"
                      type="icon"
                      onClick={() =>
                        (window.location.href = `zoomphonecall:${encodeURIComponent(
                          `${mobile?.primary?.code}+ ${mobile?.primary?.number}`,
                        )}`)
                      }
                    />
                    <span className="font-roboto ">
                      {formatPhone(
                        mobile?.primary?.code,
                        mobile?.primary?.number,
                      )}
                    </span>
                    {mobile?.primary?.number && (
                      <CopyToClipboard
                        content={
                          mobile?.primary?.code + mobile?.primary?.number
                        }
                        title="Mobile"
                        description="Copy Mobile"
                        tooltipPosition="top"
                        color="dark"
                        size="xs-sm"
                      />
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  {workAuthorization && <StatusTags tag={workAuthorization} />}
                  <StatusTags
                    tag={
                      EmployeeStatus?.find((list) => list.value === status)
                        ?.label
                    }
                    color="success"
                  />
                  <StatusTags tag="No User Account" color="gray" />
                  <span className="text-xs text-gray-500">
                    Date of Joining: {dateFormator(joinDate)}
                  </span>
                </div>
              </div>
            </OverviewCard>
          </div>
          <div className="order-4">
            {/* Column 1 — Immigration */}
            <div className="flex flex-col gap-4">
              <OverviewCard title="Immigration">
                <div className="flex flex-col gap-3">
                  {(overviewData?.immigrationCards || []).map((item, index) => (
                    <InnerCard key={index} {...item} />
                  ))}
                </div>
              </OverviewCard>
            </div>
          </div>
          <div className="order-5">
            {/* Column 2 — Documents + Time Sheets */}
            <div className="flex flex-col gap-4">
              <OverviewCard title="Documents">
                <div className="flex flex-col gap-3">
                  {(overviewData?.documentsCards || []).map((doc, index) => (
                    <InnerCard key={index} {...doc} />
                  ))}
                </div>
              </OverviewCard>

              <OverviewCard title="Time Sheets">
                <div className="flex flex-col gap-4">
                  {(overviewData?.timesheetCards || []).map(
                    ({ title, data }, i) => (
                      <div
                        key={i}
                        className="flex rounded-lg overflow-hidden bg-white shadow-sm border border-gray-100"
                      >
                        {/* Project Title */}
                        <div className="bg-orange-50 w-fit px-4 flex items-center justify-center text-sm font-semibold text-gray-700">
                          {title}
                        </div>

                        {/* Table Section */}
                        <div className="flex-1 p-2 overflow-x-auto">
                          <table className="w-full text-sm text-gray-700">
                            <thead className="text-xs text-gray-600">
                              <tr>
                                {["Month", "Hours", "Status", "Invoice"].map(
                                  (header) => (
                                    <th
                                      key={header}
                                      className="text-left py-1 px-2 font-normal"
                                    >
                                      {header}
                                    </th>
                                  ),
                                )}
                              </tr>
                            </thead>

                            <tbody>
                              {(data || []).map((row, j) => (
                                <tr
                                  key={j}
                                  className="hover:bg-gray-50 transition-colors"
                                >
                                  <td className="py-1.5 px-2 font-semibold">
                                    {row.month}
                                  </td>
                                  <td className="py-1.5 px-2 font-semibold">
                                    {row.hours}
                                  </td>
                                  <td className="py-1.5 px-2">
                                    <StatusTags
                                      tag={row.status}
                                      color={row.color}
                                    />
                                  </td>
                                  <td className="py-1.5 px-2 font-semibold">
                                    {row?.Invoice?.join(", ")}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </OverviewCard>
            </div>
          </div>
          <div className="order-6">
            <div className="flex flex-col gap-4 w-full">
              {/* Active Placement Section */}
              <OverviewCard title="Active Placement Document">
                <div className="flex flex-col ">
                  <div className="flex-1">
                    <NewTab
                      color="orange"
                      variant="text"
                      tabs={
                        overviewData?.placementCardsTabData || [
                          {
                            title: "P",
                            type: "main",
                          },
                          {
                            title: "P",
                            type: "main",
                          },
                        ]
                      }
                    />
                  </div>

                  {/* Bottom-right Days Left */}
                  {/* <div className="flex justify-end mt-1">
                  <Tag
                    title={`${placementData.daysLeft} Days Left`}
                    variant="outlined"
                    color="danger"
                    className="bg-red-200 border-red-400 "
                  />
                </div> */}
                </div>
              </OverviewCard>

              {/* Invoice Section */}
              <OverviewCard title="Invoice">
                <div className="grid gap-2">
                  {Object.entries(overviewData.invoiceCards ?? {}).map(
                    ([key, value]) => (
                      <div
                        key={key}
                        className="p-3 rounded-lg border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-center">
                          <span className="body-two font-normal text-black">
                            {key}
                          </span>
                          <span
                            className={`text-sm font-semibold text-primary`}
                          >
                            {value}
                          </span>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </OverviewCard>
            </div>
          </div>
        </div>
      </div>

      {/* <div className="flex-1 p-1 flex flex-col gap-4 mt-4 overflow-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[2fr_2fr_3fr] gap-4"></div>

        <div className="w-full">
          <div className="grid xl:grid-cols-[2fr_2fr_3fr] md:grid-cols-2 sm:grid-cols-1 gap-4 items-start ">
            
          </div>
        </div>
      </div> */}

      <Backdrop isOpen={isLoading || overviewLoading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default Overview;
